/**
 * Slice B read surfaces — pure core (no I/O, no React, no Supabase).
 *
 * Types, the typed error, the projection mappers (raw snake_case jsonb → camelCase
 * view-model), and the deterministic mock adapter. Kept import-free of the
 * supabase client / react-query so it is node-testable in isolation. The live
 * adapter, hooks, and the canonical-org-id seam live in ./sliceB.
 *
 * Contract verified from committed source @ 0512f28. Design authority:
 * design/mocks/slice-b-read-surfaces.html (owner-approved gate 2026-08-11).
 */

/* ============================================================
   View-model types
   ============================================================ */

export type WarmPathBasis = "carrier" | "practice_fallback" | "none";

/** Carrier: a real relationship carrier (active-SMD, per R-WP-3 named as such). */
export interface WarmPathCarrier {
  kind: "carrier";
  name: string;
  authorityRole: string | null;
  authorityPractice: string | null;
  relationshipContactCount: number;
  strongRelationshipCount: number;
  lastInteractionAt: string | null;
}

/** Practice fallback: a desk lead, honestly identified as a fallback (R-WP-3). */
export interface WarmPathFallback {
  kind: "practice_fallback";
  name: string;
  practice: string | null;
}

export interface WarmPath {
  basis: WarmPathBasis;
  /** null when basis === "none" (honest empty — never a fabricated warm path). */
  contact: WarmPathCarrier | WarmPathFallback | null;
  decisionReason: string;
}

export type ContactStrength = "strong" | "moderate" | "weak";

export interface StrongContact {
  contactId: string;
  name: string;
  title: string | null;
  strength: ContactStrength;
  ownerName: string | null;
  ownedByViewer: boolean;
  /** event_source / contact_origin, presented as a single readable source line. */
  source: string | null;
  matchBasis: string | null;
  interactionRecency: string | null;
  interactionCount: number;
}

/** market intel / personnel / rfp — the constellation node-color taxonomy. */
export type SignalTypeGroup = "rfp" | "personnel" | "market" | "other";

export interface RkgSignalNode {
  signalId: string;
  type: string;
  typeGroup: SignalTypeGroup;
  scfScore: number | null;
  tier: number | null;
  title: string;
  observedAt: string | null;
  lifecycleStatus: string;
  verified: boolean;
  deadlineAt: string | null;
}

export interface FactualRead {
  windowDays: number;
  activeSignalCount: number;
  distinctSignalTypeCount: number;
  strongRelationshipCount: number;
}

export interface RkgModal {
  signals: RkgSignalNode[];
  strongContacts: StrongContact[];
  factualRead: FactualRead;
}

export type CompositeStatus = "present" | "missing";

export interface CompositeFactor {
  key: "signal_strength" | "relationship_strength" | "timing_fit" | "capability_proof";
  label: string;
  status: CompositeStatus;
  /** null whenever status === "missing" — never rendered as 0. */
  value: string | number | null;
  unit?: string;
  basis: string;
  /** Optional secondary counts line (e.g. "6 relationships · 2 strong"). */
  counts?: string;
  /** 0..1 fill for a present, bar-able factor; undefined = no bar. */
  fill?: number;
}

export interface CompositeInputs {
  signalStrength: CompositeFactor;
  relationshipStrength: CompositeFactor;
  timingFit: CompositeFactor;
  /** Always status "missing" per the SQL — renders "not yet available", never 0. */
  capabilityProof: CompositeFactor;
}

export interface SliceBReadAdapter {
  getWarmPath(input: { canonicalOrgId: string; practice: string }): Promise<WarmPath>;
  getRkgModal(input: { canonicalOrgId: string; asOf?: string }): Promise<RkgModal>;
  getCompositeInputs(input: { canonicalOrgId: string; asOf?: string }): Promise<CompositeInputs>;
}

/**
 * Selects the governed practice label required by the warm-path surface.
 *
 * Signal detail classification is authoritative when available. The legacy
 * `practice` field remains a compatibility fallback for mock and older detail
 * payloads. Returning an empty string is intentional: callers must not invoke
 * the practice-scoped surface until a non-empty governed value exists.
 */
export function resolveWarmPathPractice(signal: {
  practice?: string | null;
  practiceClassification?: {
    practices: Array<{ displayName: string }>;
  } | null;
}): string {
  const classified = signal.practiceClassification?.practices[0]?.displayName
    ?.trim();
  if (classified) return classified;
  return signal.practice?.trim() ?? "";
}

/**
 * Carries the edge function's error CODE unchanged. The existing shared
 * `messageFor` helper keeps only `.message`; Slice B must surface the raw code
 * (production returns `UNAUTHORIZED_NO_AUTH_HEADER` verbatim on anon calls — owner
 * proof 2026-08-11), so this error type preserves `code` with zero normalization.
 */
export class SliceBError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status?: number,
  ) {
    super(message);
    this.name = "SliceBError";
  }
}

/* ============================================================
   Raw wire shapes (snake_case, exactly as the SQL returns them)
   ============================================================ */

export interface RawWarmPath {
  basis?: WarmPathBasis;
  decision_reason?: string;
  warm_path_contact?:
    | {
        owner_user_id?: string | null;
        name?: string | null;
        authority_role?: string | null;
        authority_practice?: string | null;
        practice?: string | null;
        relationship_contact_count?: number | null;
        strong_relationship_count?: number | null;
        last_interaction_at?: string | null;
        source?: string | null;
      }
    | null;
}
export interface RawRkgModal {
  signals?: Array<Record<string, unknown>> | null;
  strong_matched_contacts?: Array<Record<string, unknown>> | null;
  factual_read?: Record<string, unknown> | null;
}
export interface RawCompositeInputs {
  signal_strength?: Record<string, unknown> | null;
  relationship_strength?: Record<string, unknown> | null;
  timing_fit?: Record<string, unknown> | null;
  capability_proof?: Record<string, unknown> | null;
}

/**
 * Surface the edge error CODE unchanged. Two live envelope shapes exist and both
 * must pass through verbatim (verified against a live rejection 2026-08-11):
 *   - GATEWAY (verify_jwt reject, no/invalid JWT): top-level
 *     `{ "code": "UNAUTHORIZED_NO_AUTH_HEADER", "message": "Missing authorization header" }`
 *     (also mirrored on the `sb-error-code` response header).
 *   - FUNCTION (400 bad_request, function 401): nested `{ error: { code, message } }`.
 * Zero normalization: no enum mapping, no default relabelling of the code.
 */
export async function toSliceBError(error: unknown): Promise<SliceBError> {
  const ctx = (error as { context?: Response }).context;
  const status = (error as { context?: { status?: number } }).context?.status;
  const headerCode = ctx?.headers?.get?.("sb-error-code") ?? undefined;
  if (ctx && typeof ctx.json === "function") {
    try {
      const body = (await ctx.json()) as {
        error?: { code?: string; message?: string };
        code?: string;
        message?: string;
      };
      // nested (function) OR top-level (gateway) — check both, code unchanged.
      const code = body?.error?.code ?? body?.code ?? headerCode;
      const message = body?.error?.message ?? body?.message;
      if (typeof code === "string") {
        return new SliceBError(code, message ?? code, status);
      }
      if (typeof message === "string") {
        return new SliceBError("unknown", message, status);
      }
    } catch {
      /* non-JSON body — fall through to the header code / generic message */
    }
  }
  if (typeof headerCode === "string") {
    return new SliceBError(headerCode, headerCode, status);
  }
  const message = error instanceof Error ? error.message : String(error);
  return new SliceBError("unknown", message, status);
}

/* ============================================================
   Mappers (raw jsonb → view-model, defensive coercion, honest fallbacks)
   ============================================================ */

function str(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}
function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
function int0(v: unknown): number {
  return typeof v === "number" && Number.isFinite(v) ? Math.trunc(v) : 0;
}

export function groupForType(type: string | null): SignalTypeGroup {
  const t = (type ?? "").toLowerCase();
  if (t.includes("rfp")) return "rfp";
  if (t.includes("hir") || t.includes("person") || t.includes("leader") || t.includes("exec"))
    return "personnel";
  if (t.includes("m&a") || t.includes("ma_") || t.includes("market") || t.includes("acquisit"))
    return "market";
  return "other";
}

export function mapWarmPath(raw: RawWarmPath): WarmPath {
  const basis: WarmPathBasis =
    raw.basis === "carrier" || raw.basis === "practice_fallback" ? raw.basis : "none";
  const c = raw.warm_path_contact ?? null;
  let contact: WarmPath["contact"] = null;
  if (basis === "carrier" && c) {
    contact = {
      kind: "carrier",
      name: str(c.name) ?? "",
      authorityRole: str(c.authority_role),
      authorityPractice: str(c.authority_practice),
      relationshipContactCount: int0(c.relationship_contact_count),
      strongRelationshipCount: int0(c.strong_relationship_count),
      lastInteractionAt: str(c.last_interaction_at),
    };
  } else if (basis === "practice_fallback" && c) {
    contact = {
      kind: "practice_fallback",
      name: str(c.name) ?? "",
      practice: str(c.practice),
    };
  }
  return { basis, contact, decisionReason: str(raw.decision_reason) ?? "" };
}

function mapStrongContact(r: Record<string, unknown>): StrongContact {
  const owner = (r.owner ?? {}) as Record<string, unknown>;
  const source = (r.source ?? {}) as Record<string, unknown>;
  const eventSource = str(source.event_source);
  const contactOrigin = str(source.contact_origin);
  const ownerName = str(owner.name);
  return {
    contactId: str(r.contact_id) ?? "",
    name: str(r.name) ?? "",
    title: str(r.title),
    strength: (r.strength === "moderate" || r.strength === "weak" ? r.strength : "strong") as ContactStrength,
    ownerName,
    ownedByViewer: ownerName === "You",
    source: [eventSource, contactOrigin].filter(Boolean).join(" · ") || null,
    matchBasis: str(r.match_basis),
    interactionRecency: str(r.interaction_recency),
    interactionCount: int0(r.interaction_count),
  };
}

export function mapRkgModal(raw: RawRkgModal): RkgModal {
  const signals: RkgSignalNode[] = (raw.signals ?? []).map((s) => {
    const type = str(s.type) ?? "";
    return {
      signalId: str(s.signal_id) ?? "",
      type,
      typeGroup: groupForType(type),
      scfScore: num(s.scf_score),
      tier: num(s.tier),
      title: str(s.title) ?? "Untitled signal",
      observedAt: str(s.observed_at) ?? str(s.first_observed_at),
      lifecycleStatus: str(s.lifecycle_status) ?? "",
      verified:
        (str(s.lifecycle_status) ?? "").toLowerCase() === "active" && groupForType(type) === "rfp",
      deadlineAt: str(s.deadline_at),
    };
  });
  const fr = (raw.factual_read ?? {}) as Record<string, unknown>;
  return {
    signals,
    strongContacts: (raw.strong_matched_contacts ?? []).map(mapStrongContact),
    factualRead: {
      windowDays: int0(fr.window_days) || 90,
      activeSignalCount: int0(fr.active_signal_count),
      distinctSignalTypeCount: int0(fr.distinct_signal_type_count),
      strongRelationshipCount: int0(fr.strong_relationship_count),
    },
  };
}

function mapFactor(
  key: CompositeFactor["key"],
  label: string,
  raw: Record<string, unknown> | null | undefined,
  fmt: (v: unknown) => { value: string | number | null; counts?: string; fill?: number; unit?: string },
): CompositeFactor {
  const r = raw ?? {};
  const status: CompositeStatus = r.status === "present" ? "present" : "missing";
  const basis = str(r.basis) ?? "";
  if (status === "missing") {
    return { key, label, status, value: null, basis };
  }
  const { value, counts, fill, unit } = fmt(r.value);
  return { key, label, status, value, unit, basis, counts, fill };
}

export function mapCompositeInputs(raw: RawCompositeInputs): CompositeInputs {
  return {
    signalStrength: mapFactor("signal_strength", "Signal strength", raw.signal_strength, (v) => ({
      value: num(v),
      unit: "/100",
      fill: typeof num(v) === "number" ? Math.max(0, Math.min(1, (num(v) as number) / 100)) : undefined,
      counts: countsLine(raw.signal_strength),
    })),
    relationshipStrength: mapFactor(
      "relationship_strength",
      "Relationship strength",
      raw.relationship_strength,
      (v) => ({ value: str(v) ? capitalize(str(v) as string) : null, counts: relCountsLine(raw.relationship_strength) }),
    ),
    timingFit: mapFactor("timing_fit", "Timing fit", raw.timing_fit, (v) => {
      const val = (v ?? {}) as Record<string, unknown>;
      const days = num(val.days_since_latest_signal);
      return { value: days != null ? days : null, unit: days != null ? " days since latest signal" : undefined };
    }),
    // capability_proof is hard-coded status=missing / value=null in SQL.
    capabilityProof: mapFactor("capability_proof", "Capability proof", raw.capability_proof, () => ({
      value: null,
    })),
  };
}

function countsLine(raw: Record<string, unknown> | null | undefined): string | undefined {
  const r = raw ?? {};
  const active = num(r.active_signal_count);
  const scored = num(r.scored_signal_count);
  if (active == null && scored == null) return undefined;
  return `${active ?? 0} active · ${scored ?? 0} scored`;
}
function relCountsLine(raw: Record<string, unknown> | null | undefined): string | undefined {
  const r = raw ?? {};
  const total = num(r.relationship_count);
  if (total == null) return undefined;
  return `${total} relationships · ${int0(r.strong_count)} strong · ${int0(r.moderate_count)} moderate · ${int0(r.weak_count)} weak`;
}
function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/* ============================================================
   Mock adapter — deterministic per canonical org id (no Math.random)
   ============================================================ */

export function hash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
const delay = <T>(value: T, ms = 140): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms));

const CARRIERS = [
  { name: "Dara Okafor", practice: "Financial Advisory" },
  { name: "Priya Nair", practice: "Restructuring" },
  { name: "Marco Reyes", practice: "Performance Improvement" },
];
const FALLBACKS = [
  { name: "Marco Reyes", practice: "Restructuring" },
  { name: "Casey Flynn", practice: "Disputes" },
];
const CONTACTS = [
  { name: "Alex Chen", title: "Chief Financial Officer", owner: "You", source: "Web Summit 2025 · conference", basis: "Same account as this signal" },
  { name: "Priya Nair", title: "General Counsel", owner: "Dara Okafor", source: "Referral intro · referral", basis: "Colleague relationship match" },
  { name: "Sam Rivera", title: "VP Finance", owner: "You", source: "Conference 2026 · conference", basis: "Named in the source article" },
];
const SIGNAL_SEEDS = [
  { type: "ma_activity", group: "market" as SignalTypeGroup, title: "Meridian explores packaging carve-out", score: 58, tier: 3, day: "2026-03-28" },
  { type: "personnel", group: "personnel" as SignalTypeGroup, title: "New COO appointed", score: 64, tier: 2, day: "2026-04-19" },
  { type: "personnel", group: "personnel" as SignalTypeGroup, title: "VP Transformation role posted", score: 72, tier: 2, day: "2026-05-30" },
  { type: "rfp", group: "rfp" as SignalTypeGroup, title: "RFP: Finance transformation advisory", score: 87, tier: 1, day: "2026-06-24" },
];

class MockSliceBAdapter implements SliceBReadAdapter {
  getWarmPath(input: { canonicalOrgId: string; practice: string }): Promise<WarmPath> {
    const h = hash(`${input.canonicalOrgId}:wp`);
    const roll = h % 3; // 0 carrier, 1 fallback, 2 none — so honest empties are reachable
    if (roll === 0) {
      const c = CARRIERS[h % CARRIERS.length]!;
      return delay<WarmPath>({
        basis: "carrier",
        decisionReason: "strongest_active_smd_carrier",
        contact: {
          kind: "carrier",
          name: c.name,
          authorityRole: "SMD",
          authorityPractice: c.practice,
          relationshipContactCount: 3 + (h % 3),
          strongRelationshipCount: 2,
          lastInteractionAt: "2026-07-28",
        },
      });
    }
    if (roll === 1) {
      const f = FALLBACKS[h % FALLBACKS.length]!;
      return delay<WarmPath>({
        basis: "practice_fallback",
        decisionReason: "no_active_smd_carrier",
        contact: { kind: "practice_fallback", name: f.name, practice: f.practice },
      });
    }
    return delay<WarmPath>({
      basis: "none",
      decisionReason: "no_active_smd_carrier_no_fallback",
      contact: null,
    });
  }

  getRkgModal(input: { canonicalOrgId: string }): Promise<RkgModal> {
    const h = hash(`${input.canonicalOrgId}:rkg`);
    const signalCount = 2 + (h % 3); // 2..4 signals
    const seeds = SIGNAL_SEEDS.slice(0, signalCount);
    const signals: RkgSignalNode[] = seeds.map((s, i) => ({
      signalId: `${input.canonicalOrgId}-s${i}`,
      type: s.type,
      typeGroup: s.group,
      scfScore: s.score,
      tier: s.tier,
      title: s.title,
      observedAt: `${s.day}T09:00:00Z`,
      lifecycleStatus: "active",
      verified: s.group === "rfp",
      deadlineAt: s.group === "rfp" ? "2026-09-12T00:00:00Z" : null,
    }));
    const strongN = h % 4 === 0 ? 0 : 1 + (h % 2); // sometimes 0 strong (honest thin)
    const strongContacts: StrongContact[] = CONTACTS.slice(0, strongN).map((c, i) => ({
      contactId: `${input.canonicalOrgId}-c${i}`,
      name: c.name,
      title: c.title,
      strength: "strong",
      ownerName: c.owner,
      ownedByViewer: c.owner === "You",
      source: c.source,
      matchBasis: c.basis,
      interactionRecency: "2026-08-04",
      interactionCount: 3 + i,
    }));
    const distinctTypes = new Set(signals.map((s) => s.type)).size;
    return delay<RkgModal>({
      signals,
      strongContacts,
      factualRead: {
        windowDays: 90,
        activeSignalCount: signals.length,
        distinctSignalTypeCount: distinctTypes,
        strongRelationshipCount: strongContacts.length,
      },
    });
  }

  getCompositeInputs(input: { canonicalOrgId: string }): Promise<CompositeInputs> {
    const h = hash(`${input.canonicalOrgId}:ci`);
    const scored = h % 5 !== 0; // sometimes nothing scored → signal strength missing (honest)
    const topScore = 60 + (h % 40);
    const strong = 1 + (h % 3);
    const moderate = h % 2;
    const weak = h % 4;
    const relTotal = strong + moderate + weak;
    const label = strong > 0 ? "Strong" : moderate > 0 ? "Moderate" : relTotal > 0 ? "Weak" : null;
    const days = 20 + (h % 60);
    return delay<CompositeInputs>({
      signalStrength: scored
        ? {
            key: "signal_strength",
            label: "Signal strength",
            status: "present",
            value: topScore,
            unit: "/100",
            fill: topScore / 100,
            basis: "Strongest active signal in the 90-day window.",
            counts: `${2 + (h % 3)} active · ${2 + (h % 3)} scored`,
          }
        : { key: "signal_strength", label: "Signal strength", status: "missing", value: null, basis: "No scored signal in the 90-day window." },
      relationshipStrength:
        relTotal > 0
          ? {
              key: "relationship_strength",
              label: "Relationship strength",
              status: "present",
              value: label,
              basis: "Deployed contact-strength taxonomy at this account.",
              counts: `${relTotal} relationships · ${strong} strong · ${moderate} moderate · ${weak} weak`,
            }
          : { key: "relationship_strength", label: "Relationship strength", status: "missing", value: null, basis: "No relationships on record at this account." },
      timingFit: {
        key: "timing_fit",
        label: "Timing fit",
        status: "present",
        value: days,
        unit: " days since latest signal",
        basis: `Latest signal within the 90-day window.`,
      },
      // Always missing — the SQL hard-codes status=missing / value=null.
      capabilityProof: {
        key: "capability_proof",
        label: "Capability proof",
        status: "missing",
        value: null,
        basis: "Delivery Graph capability proof is not yet available.",
      },
    });
  }
}

/** A fresh mock adapter (used by the env selector in ./sliceB and by tests). */
export function createMockSliceBAdapter(): SliceBReadAdapter {
  return new MockSliceBAdapter();
}
